import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import HookForm from '../../../../components/HookForm/HookForm';
import Button from '../../../../components/Button/Button';
import Loader from '../../../../components/Loader/Loader';
import LoaderInline from '../../../../components/Loader/LoaderInline';
import formStyles from '../../../../components/Form/form.module.css';

import SellerBlock from './SellerBlock';
import { buildCarInfoInputs } from '../carInfoInputs';
import ensureComplectation from '../ensureComplectation';
import useIbanAutofill from '../../../../hooks/useIbanAutofill';
import { shortFormsEnabled } from '../../../../brand/tenant';

import createCar from '../../../../api/cars/createCar';
import createConsignment from '../../../../api/cars/createConsignment';
import getDcRequisites from '../../../../api/cars/getDcRequisites';
import getDcList from '../../../../api/cars/getDcList';
import ensureContainer from '../../../../api/cars/ensureContainer';
import getItems from '../../../../api/cars/getItems';

import alert from '../../../../helpers/alert';
import extractErrorMessage from '../../../../utils/extractErrorMessage';
import { CAR_ORIGIN_TYPES, originLabel } from '../../../../constants/carOrigin';
import Select from '../../../../components/Select/Select';
import { textStyles } from '../../../../constants/textStyles';
import { consignmentAnyDcEnabled } from '../../../../brand/tenant';
import {
  REQUISITES_FIELDS,
  REQUISITES_INPUTS,
  SELLER_REQUISITES_INPUTS,
  agentRequisitesComplete,
  agentRequisitesOf,
  autoSellerDc,
  buildSellerDcInput,
  cleanBank,
  hideInputs,
  requisitesTitle,
  sellerAccountComplete,
  sellerAccountOf,
  sellerFio,
} from './requisitesInputs';

// minmax(0, 1fr), а не 1fr: иначе длинное значение («По договору поручения, первичная регистрация»)
// раздвигает свою колонку и сдвигает остальные.
const carGrid = {
  display: 'grid',
  gap: '20px',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  alignItems: 'flex-end',
};
// На экране сделки форма уже колонки: в четыре ряда подписи и значения не помещались.
const carGridEmbedded = { ...carGrid, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' };
const reqGrid = { display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr 1fr' };
const reqOverlay = {
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  background: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * Постановка б/у автомобиля по договору поручения — один экран без шагов:
 * данные ТС, продавец (клиент CRM), реквизиты ДЦ и счёт продавца.
 * У серого дилера продавец — сам ДЦ: один селект вместо клиента и счёта, агент — тот же ДЦ,
 * его реквизиты подставлены из карточки, поэтому блоки счёта и реквизитов скрыты.
 * После сохранения менеджер сразу переходит к созданию сделки; если экран открыт поверх
 * сделки (onCreated), он, наоборот, отдаёт ей созданное авто и ничего не уводит со страницы.
 */
/**
 * embedded — форма встроена в экран сделки: без своей кнопки и без поля VIN (его вводит экран сделки и
 * передаёт в vin), сохранение запускается снаружи через ref.submit(), который отдаёт созданное авто или null.
 */
const ConsignmentCreate = forwardRef(({ originInput, originCarId = CAR_ORIGIN_TYPES.CONSIGNMENT, onCreated, vin, onVinChange, embedded = false }, ref) => {
  const navigate = useNavigate();
  const sellerIsDc = shortFormsEnabled();

  const carFormRef = useRef(null);
  const reqFormRef = useRef(null);
  const sellerReqFormRef = useRef(null);
  const sellerIbanAutofill = useIbanAutofill(sellerReqFormRef);
  // Реквизиты ДЦ обычно приезжают с бэка целиком; подстановка сработает
  // только там, где банк и БИК остались пустыми.
  const dcIbanAutofill = useIbanAutofill(reqFormRef);
  const sellerRef = useRef(null);

  const [containerId, setContainerId] = useState(null);
  const [seller, setSeller] = useState(null);
  const [sellerError, setSellerError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingRequisites, setLoadingRequisites] = useState(false);
  // Бэк подставил реквизиты посредника контура вместо реквизитов выбранного ДЦ
  const [viaIntermediary, setViaIntermediary] = useState(false);
  const [agentRequisites, setAgentRequisites] = useState(null);
  const [carId, setCarId] = useState(null);
  // Город выбранного ДЦ: у серого дилера он же город автомобиля, поле «Город» скрыто.
  const [dcCityId, setDcCityId] = useState(null);
  // Счёт ДЦ из его карточки пришёл целиком — поля счёта прячем; неполный — показываем, дозаполнить придётся руками.
  const [sellerAccountShown, setSellerAccountShown] = useState(false);
  // ДЦ подобран сам (у менеджера он один, не считая посредника) — селект прячем.
  const [dcAuto, setDcAuto] = useState(false);
  // Пока список ДЦ не загрузился, не знаем, нужен ли селект: блок продавца не показываем, чтобы он не мигал.
  const [dcResolved, setDcResolved] = useState(!sellerIsDc);

  // Реквизиты берём ровно у выбранного ДЦ автомобиля — ничего не подставляем
  // по ДЦ менеджера, пока менеджер сам не выбрал ДЦ.
  const handleDcChange = async (selected) => {
    const list = Array.isArray(selected) ? selected : [selected];
    const dcId = list[list.length - 1]?.value;
    if (!dcId) return;
    setDcCityId(list[list.length - 1]?.cityId ?? null);

    setLoadingRequisites(true);
    const res = await getDcRequisites(dcId).catch(() => null);
    const requisites = res?.data || {};
    // Серый дилер продаёт сам и агент у него тот же ДЦ: берём его собственные реквизиты, не посредника.
    const agentSource = sellerIsDc ? requisites.own || {} : requisites;
    setViaIntermediary(sellerIsDc ? false : !!requisites.viaIntermediary);
    setAgentRequisites(agentRequisitesOf(agentSource));
    Object.entries(REQUISITES_FIELDS).forEach(([field, key]) =>
      reqFormRef.current?.setValue(field, agentSource?.[key] ?? '')
    );
    // Продавец — этот же ДЦ: его счёт подставляем в форму сразу, менеджер может поправить.
    if (sellerIsDc) {
      Object.entries(sellerAccountOf(requisites.own)).forEach(([field, value]) =>
        sellerReqFormRef.current?.setValue(field, value)
      );
      setSellerAccountShown(!sellerAccountComplete(requisites.own));
    }
    setLoadingRequisites(false);
  };

  // Комплектации висят на контейнере (пара марка+модель), а у б/у-марок и
  // моделей, заведённых менеджером здесь же, контейнера ещё нет — бэк отдаёт
  // существующий или заводит недостающий.
  // ponytail: контейнер заводится сразу при выборе модели, поэтому брошенная
  // форма оставляет пустой контейнер. В витрину он не попадает (там фильтр по
  // стоку), чистить — только если такие записи начнут мешать в /cars/containers.
  const resolveContainerId = async (modelOption, markOption) => {
    if (!markOption?.value || !modelOption?.value) {
      setContainerId(null);
      return null;
    }
    try {
      const res = await ensureContainer({ markId: markOption.value, modelId: modelOption.value });
      const id = res?.data?.id ?? null;
      setContainerId(id);
      return id;
    } catch (e) {
      // Молча глотать нельзя: без контейнера пустеет список комплектаций и
      // пропадает кнопка «Добавить комплектацию» — выглядит как сломанная
      // вёрстка, хотя на деле не ответил бэк.
      setContainerId(null);
      alert.error(
        extractErrorMessage(e?.response?.data) ||
          'Не удалось определить контейнер по марке и модели — комплектации недоступны'
      );
      return null;
    }
  };

  // У серого дилера форма выглядит так же, как обычная: происхождение в той же сетке сразу за VIN,
  // а не отдельной карточкой сверху — при выборе происхождения ничего не прыгает. Обработчик берём
  // из ref: инпуты собраны один раз, а замыкание CarCreate обновляется каждый рендер.
  const originInputRef = useRef(originInput);
  originInputRef.current = originInput;

  const carInputs = useMemo(() => {
    const inputs = buildCarInfoInputs({
        // VIN, введённый до выбора происхождения, переезжает сюда вместе с формой.
        carData: { vin },
        onMarkChange: () => setContainerId(null),
        onModelChange: resolveContainerId,
        onDcChange: handleDcChange,
        singleDc: true,
        usedDcOnly: !consignmentAnyDcEnabled(),
        hideDc: sellerIsDc,
        // Город берём у ДЦ; если в его карточке города нет — поле остаётся, заполнить придётся руками.
        hideCity: sellerIsDc && !!dcCityId,
        vinFirst: true,
        onPickVinCar: pickExistingCar,
        getContainerId: () => containerId,
        withTechPassport: true,
        mileageRequired: true,
        usedDictionaries: true,
        shortFields: shortFormsEnabled(),
    });
    // Встроенная форма: VIN вводит экран сделки, а происхождение выбирается здесь же — из двух вариантов поручения.
    if (embedded) {
      const origin = {
        component: Select,
        name: 'origin',
        label: 'Происхождение автомобиля',
        placeholder: 'Выберите тип происхождения',
        options: [CAR_ORIGIN_TYPES.CONSIGNMENT, CAR_ORIGIN_TYPES.CONSIGNMENT_FIRST_REG].map((value) => ({
          value,
          label: originLabel(value, true),
        })),
        defaultValue: { value: CAR_ORIGIN_TYPES.CONSIGNMENT, label: originLabel(CAR_ORIGIN_TYPES.CONSIGNMENT, true) },
        rules: { required: true },
      };
      return [origin, ...inputs.filter((input) => input.name !== 'vin')];
    }
    if (!sellerIsDc || !originInput) return inputs;
    const origin = {
      ...originInput,
      onChangeCustom: (...args) => originInputRef.current?.onChangeCustom?.(...args),
    };
    return [inputs[0], origin, ...inputs.slice(1)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, dcCityId]);

  const sellerInputs = useMemo(
    () =>
      sellerIsDc
        ? [
            ...hideInputs([buildSellerDcInput({ onChange: handleDcChange, used: !consignmentAnyDcEnabled() })], dcAuto),
            ...hideInputs(SELLER_REQUISITES_INPUTS, !sellerAccountShown),
          ]
        : SELLER_REQUISITES_INPUTS,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sellerAccountShown, dcAuto]
  );

  // Серому дилеру выбирать ДЦ не нужно: берём его собственный, реквизиты подтянутся сами.
  useEffect(() => {
    if (!sellerIsDc) return;
    getDcList({ used: !consignmentAnyDcEnabled() || undefined })
      .then((res) => {
        const auto = autoSellerDc(res?.data);
        if (!auto) return;
        setDcAuto(true);
        sellerReqFormRef.current?.setValue('dc', auto);
        handleDcChange(auto);
      })
      .catch(() => {})
      .finally(() => setDcResolved(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // У серого дилера реквизиты Агента — это карточка того же ДЦ, из неё они подставлены: менеджеру показывать
  // и править там нечего. Блок появляется, только когда карточка загружена и в ней чего-то не хватает.
  const hideAgentBlock = sellerIsDc && !(agentRequisites && !agentRequisitesComplete(agentRequisites));

  const validateCar = (values, resolvedContainerId) => {
    if (values?.vin?.trim()?.length !== 17) {
      alert.error('Длина VIN должна быть 17 символов');
      return false;
    }
    if (values?.issueYear > new Date().getFullYear() || values?.issueYear < 1900) {
      alert.error('Проверьте год выпуска авто');
      return false;
    }
    if (!resolvedContainerId) {
      alert.error('Не удалось определить контейнер по выбранной марке/модели');
      return false;
    }
    return true;
  };

  const saveCar = async (values, resolvedContainerId, sellerDcId) => {
    if (carId) return carId;

    // На короткой форме комплектации на экране нет: подбираем её по типу
    // двигателя сами, иначе машину не добавить в сделку.
    const complectationId = shortFormsEnabled()
      ? await ensureComplectation({
          containerId: resolvedContainerId,
          engineType: values?.engineType?.value,
        })
      : values?.complectationId?.value;

    const payload = {
      phid: 5,
      originCarId: values?.origin?.value ?? originCarId,
      used: true,
      containerId: resolvedContainerId,
      markId: values?.mark?.value,
      modelId: values?.model?.value,
      complectationId,
      transmissionId: values?.transmissionId?.value,
      issueYear: values?.issueYear,
      vin: values?.vin?.trim(),
      color: values?.color?.value,
      price: parseInt(String(values?.price).replace(/[^\d]/g, ''), 10),
      engine_volume: values?.engine_volume,
      powerId: values?.power,
      wdId: values?.wdId?.value,
      cityId: values?.cityId?.value ?? dcCityId ?? undefined,
      dcIds: sellerDcId
        ? [sellerDcId]
        : []
            .concat(values?.dc || [])
            .map((dc) => dc?.value)
            .filter((v) => v != null),
      mileage: values?.mileage ? parseInt(values.mileage, 10) : undefined,
      engineType: values?.engineType?.value,
      texNumber: values?.texNumber?.trim() || undefined,
      texDate: values?.texDate ? dayjs(values.texDate).format('YYYY-MM-DD') : undefined,
      paid: 5124,
      paidTypeId: 1,
      countryId: 113,
    };

    const data = new FormData();
    data.append('json', JSON.stringify(payload));

    const response = await createCar(data);
    const id = response?.data?.car?.id || response?.data?.id;
    setCarId(id);
    return id;
  };

  const handleSubmit = async () => {
    if (sellerIsDc && loadingRequisites) {
      alert.error('Реквизиты дилерского центра ещё загружаются — повторите через секунду');
      return null;
    }
    if (!sellerIsDc) {
      if (!seller?.value) {
        setSellerError('Выберите или создайте продавца');
        sellerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return null;
      }
      setSellerError('');
    }

    const sellerRequisites = await sellerReqFormRef.current.submit();
    if (!sellerRequisites) return null;
    const sellerDc = sellerIsDc ? sellerRequisites.dc : null;

    const submittedCar = await carFormRef.current.submit();
    if (!submittedCar) return null;
    const carValues = embedded ? { ...submittedCar, vin } : submittedCar;

    // Менеджер может нажать «Сохранить» раньше, чем ответит ensureContainer по
    // только что выбранной модели — дожимаем контейнер здесь, а не падаем в
    // «не удалось определить».
    const resolvedContainerId = containerId || (await resolveContainerId(carValues?.model, carValues?.mark));
    if (!validateCar(carValues, resolvedContainerId)) return null;

    let requisites = agentRequisites;
    if (hideAgentBlock) {
      if (loadingRequisites || !requisites) {
        alert.error('Реквизиты дилерского центра ещё загружаются — повторите через секунду');
        return null;
      }
    } else {
      requisites = await reqFormRef.current.submit();
      if (!requisites) return null;
    }

    setSaving(true);
    try {
      const id = await saveCar(carValues, resolvedContainerId, sellerDc?.value);
      if (!id) throw new Error('Не удалось создать автомобиль');

      await createConsignment({
        carId: id,
        ...(sellerDc ? { sellerDcId: sellerDc.value } : { sellerClientId: seller.value }),
        price: parseInt(String(carValues?.price).replace(/[^\d]/g, ''), 10),
        requisites,
        sellerRequisites: {
          recipient: sellerRequisites.recipient,
          iban: cleanBank(sellerRequisites.iban),
          bankName: sellerRequisites.bankName,
          bic: cleanBank(sellerRequisites.bic),
        },
      });

      return (await goToDeal(id, sellerDc)) ?? null;
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || e?.message || 'Не удалось оформить поручение');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Встроенная форма сохраняется снаружи: экран сделки зовёт submit() и получает созданное авто или null.
  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  // Менеджер выбрал в подсказке по VIN уже заведённую машину — вторую карточку не заводим,
  // уводим в сделку с этой.
  async function pickExistingCar(found) {
    const res = await getItems({ id: found.id }).catch(() => null);
    const car = res?.data?.cars?.[0];
    if (!car) {
      navigate(`/cars/car/${found.id}/main-data`);
      return;
    }
    // Поверх сделки отдаём машину ей же, а не уводим со страницы.
    if (onCreated) {
      onCreated(car);
      return;
    }
    navigate('/deals/create', { state: { car } });
  }

  // Сразу уводим в создание сделки с этим авто. Карточку тянем с бэка, чтобы
  // экран сделки получил ровно тот же объект, что и при продаже из карточки авто.
  const goToDeal = async (id, sellerDc) => {
    const res = await getItems({ id }).catch(() => null);
    const car = res?.data?.cars?.[0];

    if (onCreated || embedded) {
      if (!car) {
        alert.error('Автомобиль создан, но не удалось его загрузить — найдите его в списке и добавьте в сделку');
        return null;
      }
      const created = {
        ...car,
        consignment:
          car?.consignment ||
          (sellerDc
            ? { sellerDc: { id: sellerDc.value, name: sellerDc.label } }
            : { seller: seller?.client || { name: seller?.label } }),
      };
      if (onCreated) onCreated(created);
      return created;
    }

    if (!car) {
      alert.success('Автомобиль принят по договору поручения');
      navigate(`/cars/car/${id}/main-data`);
      return;
    }

    navigate('/deals/create', {
      state: {
        car: {
          ...car,
          consignment:
            car?.consignment ||
            (sellerDc
              ? { sellerDc: { id: sellerDc.value, name: sellerDc.label } }
              : { seller: seller?.client || { name: seller?.label } }),
        },
      },
    });
  };

  return (
    <div style={{ paddingBottom: 20 }}>
      {/* Форму не размонтируем на время сохранения: при ошибке бэка введённое
          не должно пропадать. */}
      {saving && !embedded && <Loader />}

      {/* Происхождение остаётся наверху, где менеджер его и выбрал. */}
      {originInput && !sellerIsDc && (
        <HookForm border inputs={[originInput]} grid={{ display: 'grid', maxWidth: 420 }} />
      )}

      {!sellerIsDc && (
        <div className={formStyles.borderWrapper} ref={sellerRef}>
          <p style={textStyles.formTitle}>1. Продавец</p>
          <div style={{ marginTop: 20 }}>
            <SellerBlock
              seller={seller}
              onChange={(value) => {
                setSeller(value);
                setSellerError('');
                const fio = sellerFio(value);
                if (fio) sellerReqFormRef.current?.setValue('recipient', fio);
              }}
              error={sellerError}
            />
          </div>
        </div>
      )}

      {/* ДЦ подобран сам и счёт в карточке полный — блок пуст, но остаётся смонтированным: в нём лежат значения. */}
      <div style={sellerIsDc && (!dcResolved || (dcAuto && !sellerAccountShown)) ? { display: 'none' } : undefined}>
        <HookForm
          ref={sellerReqFormRef}
          border
          title={sellerIsDc ? 'Продавец — дилерский центр' : '2. Счёт продавца'}
          inputs={sellerInputs}
          grid={reqGrid}
          onValuesChange={sellerIbanAutofill}
        />
      </div>

      <HookForm
        ref={carFormRef}
        border
        title={embedded ? 'Данные нового автомобиля' : sellerIsDc ? 'Информация об автомобиле' : '3. Данные автомобиля'}
        inputs={carInputs}
        grid={embedded ? carGridEmbedded : carGrid}
        onValuesChange={(values) => onVinChange && onVinChange(values?.vin || '')}
      />

      {/* Форму не подменяем лоадером: она должна остаться смонтированной, чтобы
          ответ по ДЦ было куда записать. Вместо этого гасим блок оверлеем —
          customDataTop рендерится внутри карточки, поэтому inset считается от неё. */}
      {/* Блок остаётся смонтированным и просто скрыт: ответ по ДЦ записывается в него в любом случае. */}
      <div style={hideAgentBlock ? { display: 'none' } : undefined}>
        <HookForm
          ref={reqFormRef}
          border
          title={requisitesTitle(viaIntermediary)}
          inputs={REQUISITES_INPUTS}
          grid={reqGrid}
          onValuesChange={dcIbanAutofill}
          style={{ position: 'relative' }}
          customDataTop={
            loadingRequisites
              ? () => (
                  <div style={reqOverlay}>
                    <LoaderInline />
                  </div>
                )
              : undefined
          }
        />
      </div>

      {!embedded && (
        <Button
          text={onCreated ? 'Сохранить и добавить в сделку' : 'Сохранить и перейти к сделке'}
          fill
          onClick={handleSubmit}
          disabled={saving}
        />
      )}
    </div>
  );
});

export default ConsignmentCreate;
